import { FileDownloader } from '../common/download';
import { MonthlyScheduleRecord } from './models';
import { QueryDef } from './../services/query-def';
import { DataManagerService } from './../services/data-manager.service';
import { Injectable } from '@angular/core';
import { WebApiService } from '../services/web-api.service';
import { BaseEntityService } from '../services/base-entity.service';
import { HttpParams } from '@angular/common/http';

import { QueryResult, Predicate } from 'breeze-client';
import { notEmpty } from '../common/util';
import { convertValueToLuxon } from '@common/util/date-time-formatting/convert-value-to-luxon';
import { DailyScheduleRecord } from './models/daily-schedule-record';

@Injectable()
export class ScheduleService extends BaseEntityService {

    private fileDownloader: FileDownloader = new FileDownloader();

    readonly COMPONENT_LOG_TAG = 'schedule-service';

    constructor(
        private dataManager: DataManagerService,
        private webApiService: WebApiService
    ) {
        super();
    }

    async getScheduleNonTasks(queryDef: QueryDef): Promise<QueryResult> {
        let query = this.buildDefaultQuery('ScheduleNonTasks', queryDef);

        query = query.expand(queryDef.expands.join(','));

        let predicates: Predicate[] = [];
        if (queryDef.filter) {
            predicates = predicates.concat(this.buildPredicates(queryDef.filter));

            if (notEmpty(predicates)) {
                query = query.where(Predicate.and(predicates));
            }
        }

        return this.dataManager.executeQuery(query)
            .catch(this.dataManager.queryFailed);
    }

    buildPredicates(filter: any): Predicate[] {
        const predicates: Predicate[] = [];

        if (!filter) {
            return predicates;
        }

        // return no results if these filters are applied
        // they relate to workflow tasks only
        if (filter.jobs ||
            filter.TaskLocation ||
            filter.MaterialLocation ||
            filter.WorkflowTaskKeys ||
            filter.TaskStatusKeys ||
            filter.JobCreatedBy
        ) {
            predicates.push(
                Predicate.create('C_ScheduleNonTask_key', 'eq', null)
            );
        }

        if (notEmpty(filter.ResourceKeys)) {
            predicates.push(
                Predicate.create('C_Resource_key', 'in', filter.ResourceKeys)
            );
        }

        if (notEmpty(filter.ResourceGroupKeys)) {
            predicates.push(
                Predicate.create('Resource.C_ResourceGroup_key', 'in', filter.ResourceGroupKeys)
            );
        }

        if (filter.DateDueStart || filter.DateDueEnd) {
            const datePredicates: Predicate[] = [];

            if (filter.DateDueEnd) {
                const dateEnd = convertValueToLuxon(filter.DateDueEnd).startOf('day')
                    .plus({"day": 1})
                    .toJSDate();

                datePredicates.push(
                    Predicate.create('DateTimeStart', '<', dateEnd)
                );
            }

            if (filter.DateDueStart) {
                datePredicates.push(
                    Predicate.create('DateTimeComplete', '>=', filter.DateDueStart)
                );
            }

            if (notEmpty(datePredicates)) {
                predicates.push(Predicate.and(datePredicates));
            }
        }

        return predicates;
    }


    async getMonthlySchedule(paramsObject: any): Promise<MonthlyScheduleRecord[]> {
        paramsObject.UtcOffset = new Date().getTimezoneOffset() / 60;
        const params: HttpParams = this.webApiService.buildURLSearchParams(paramsObject, false);

        const requestUrl = 'api/schedule/monthly';
        const response = await this.webApiService.callApi(requestUrl, params);
        return response.data.results as MonthlyScheduleRecord[];
    }

    async getDailySchedule(paramsObject: any): Promise<DailyScheduleRecord[]> {
        paramsObject.UtcOffset = new Date().getTimezoneOffset() / 60;
        const params: HttpParams = this.webApiService.buildURLSearchParams(paramsObject, false);

        const requestUrl = 'api/schedule/taskAppointments';
        const response = await this.webApiService.callApi(requestUrl, params);
        return response.data.results as DailyScheduleRecord[];
    }

    async downloadICSFile(paramsObject: any, filename: string): Promise<void> {
        paramsObject.UtcOffset = new Date().getTimezoneOffset() / 60;
        const params: HttpParams = this.webApiService.buildURLSearchParams(paramsObject, false);

        const requestUrl = 'api/schedule/export/ics';
        const mimeType = 'text/calendar';
        const response = await this.webApiService.callTextApi(requestUrl, params);
        this.fileDownloader.triggerFileDownload(response.data, filename, mimeType);
    }

    createScheduleNonTask(initialValues: any): any {
        return this.dataManager.createEntity('ScheduleNonTask', initialValues);
    }

    deleteScheduleNonTask(scheduleNonTask: any): void {
        this.dataManager.deleteEntity(scheduleNonTask);
    }

    cancelScheduleNonTask(scheduleNonTask: any): void {
        if (!scheduleNonTask) {
            return;
        }

        if (scheduleNonTask.C_ScheduleNonTask_key > 0) {
            this.cancelScheduleNonTaskEdits(scheduleNonTask);
        } else {
            this.cancelNewScheduleNonTask(scheduleNonTask);
        }
    }

    private cancelNewScheduleNonTask(scheduleNonTask: any): void {
        try {
            this.deleteScheduleNonTask(scheduleNonTask);
        } catch (error) {
            console.error('Error cancelling new ScheduleNonTask: ' + error);
        }
    }

    private cancelScheduleNonTaskEdits(scheduleNonTask: any): void {
        this.dataManager.rejectEntityAndRelatedPropertyChanges(scheduleNonTask);
    }
}
